import { CONSENT_TO_TREAT_KEY } from '@act/shared/constants';
import CloseIcon from '@mui/icons-material/Close';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Fragment, useCallback, useState } from 'react';
import { CloseButton } from 'react-components/dialog-forms/components/SharedDialogHookComponents';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import { selectSelectedMember } from 'redux/slices/memberSlice';
import { avonPatientRequests } from 'redux/requests/avon-patient.requests';
import { consentAppSelectors } from 'redux/slices/consent.slice';
import { useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { themeColors } from 'toolkit/styles/colors';
import { AvonUrlHelper } from 'utilities/avonUrls';
import { Button, FullscreenLoader } from '@act/storybook';

const useStartProviderScheduling = () => {
  const selectedMember = useAppSelector(selectSelectedMember);
  const consentToTreatTerm =
    consentAppSelectors.useSelectTermByKey(CONSENT_TO_TREAT_KEY);
  const [_avonResponse, isAvonLoading, createAvonPatient] = useApiCall(
    avonPatientRequests.createAvonPatient
  );
  const [learnMoreOpen, setLearnMoreOpen] = useState<boolean>(false);

  const confirmDialog = useConfirmationAlertDialog();

  const startProviderScheduling = useCallback(async () => {
    if (
      consentToTreatTerm?.consented === null ||
      consentToTreatTerm?.consented === undefined
    ) {
      const continueAnyways = await confirmDialog({
        title: '',
        message: (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              textAlign: 'center',
            }}
          >
            <Typography variant="titleMedium" component="h1">
              Consent to treat is incomplete
            </Typography>
            <Typography variant="bodyMedium" component="p">
              The member will have to complete the agreement prior to the first
              provider appointment
            </Typography>
            <Button
              sx={{ color: themeColors.onSurface }}
              onClick={() => {
                setLearnMoreOpen(true);
              }}
            >
              Learn more
            </Button>
          </Box>
        ),
        icon: <ReportProblemOutlinedIcon />,
        iconColor: 'warning',
        cancelText: 'Go to consents',
        confirmationText: 'Continue to provider scheduling',
        showClose: true,
      });
      if (!continueAnyways) {
        return;
      }
    }
    if (!selectedMember.avonId) {
      await createAvonPatient({
        params: {},
        body: { memberId: selectedMember.id },
      });
    }
    const url = AvonUrlHelper.getAvonCalendarUrl();
    window.open(url, '_blank', 'noopener noreferrer');
  }, [
    learnMoreOpen,
    setLearnMoreOpen,
    selectedMember,
    consentToTreatTerm,
    confirmDialog,
  ]);

  const component = (
    <Fragment>
      <FullscreenLoader open={!!isAvonLoading} />
      <Dialog open={learnMoreOpen} onClose={() => setLearnMoreOpen(false)}>
        <DialogTitle>
          Learn more{' '}
          <CloseButton
            aria-label="close"
            onClick={() => setLearnMoreOpen(false)}
          >
            <CloseIcon />
          </CloseButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography variant="titleMedium" component="h1">
              The consents Reema requires
            </Typography>
            <Typography variant="subheadMedium" component="h2">
              TCPA agreement
            </Typography>
            <Typography variant="bodyMedium" component="p">
              The Telephonic Consumer Protection Act (TCPA) limits companies
              from calling clients or prospective customers using automatic
              dialing systems, artificial or prerecorded voice messages, SMS
              text messages, and fax machines.
            </Typography>
            <Typography variant="subheadMedium" component="h2">
              Communicate PHI electronically consent
            </Typography>
            <Typography variant="bodyMedium" component="p">
              We ask for consent to discuss our members’ PHI via electronic
              means. This primarily applies to text communication, as it is very
              difficult to carry on a meaningful conversation with a member
              without disclosing PHI of some sort.
            </Typography>
            <Typography variant="subheadMedium" component="h2">
              Consent to treat
            </Typography>
            <Typography variant="bodyMedium" component="p">
              This is required in order for members to work with a Reema
              therapist or prescriber. Their first appointment cannot occur
              without a completed consent to treat.
            </Typography>
            <Typography variant="subheadMedium" component="h2">
              What you should do
            </Typography>
            <Typography variant="bodyMedium" component="p">
              Complete consents for this member by clicking on consents listed
              in the member profile. Reach out to your manager for further
              assistance.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
  return { component, startProviderScheduling };
};

export default useStartProviderScheduling;
